import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPencil,
  faTrash,
} from "@fortawesome/free-solid-svg-icons";
import { Container } from "components";
import Pagination from "components/pagination";
import { useToaster } from "hooks";
import moment from "moment";
import api from "services/api";

export const ServicesList = () => {
  const [services, setServices] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState({ total: 0, per_page: 10 });
  const [search, setSearch] = useState("");
  const navigate = useNavigate();
  const [deleteModal, setDeleteModal] = useState(false);
  const [selectedServiceId, setSelectedServiceId] = useState(null);
  const { setErrorToaster, setSuccessToaster } = useToaster();

  const fetchServices = async (page = 1, query = "") => {
    try {
      const searchParam = query ? `&search=${encodeURIComponent(query)}` : "";
      const response = await api.get(`/industries?page=${page}${searchParam}`);
      const data = response?.data?.data || [];
      setServices(Array.isArray(data) ? data : []);
      setCurrentPage(page);
      // Handle pagination if API returns pagination object
      if (response?.data?.pagination) {
        setPagination(response.data.pagination);
      }
      console.log("fetchServices list:", data);
    } catch (error) {
      console.log("fetchServices errors:", error);
      setErrorToaster("Failed to fetch services");
    }
  };

  useEffect(() => {
    fetchServices(1, search);
  }, []);

  const handleSearch = () => {
    const query = search.trim();
    fetchServices(1, query);
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      handleSearch();
    }
  };

  const openDeleteModal = (id) => {
    setSelectedServiceId(id);
    setDeleteModal(true);
  };

  const handleDeleteModelConfirmation = async () => {
    if (selectedServiceId) {
      try {
        const response = await api.delete(`/industries/${selectedServiceId}`);
        console.log("handleDeleteModelConfirmation:", response);
        setDeleteModal(false);
        setSelectedServiceId(null);
        fetchServices(currentPage, search);
        setSuccessToaster("Successfully deleted service");
      } catch (error) {
        console.log("handleDeleteModelConfirmation Error:", error);
        setErrorToaster(error?.response?.data?.message || "Failed to delete service");
      }
    }
  };

  const getStatusBadge = (status) => {
    // API returns status as "1" for active, but we'll check both formats
    const isActive = status === "1" || status === 1 || status === true || status === "Active";
    return isActive ? "bg-success" : "bg-secondary";
  };

  const getStatusText = (status) => {
    const isActive = status === "1" || status === 1 || status === true || status === "Active";
    return isActive ? "Active" : "Inactive";
  };

  return (
    <Container>
      <div className="content-wrapper">
        <div
          className="container-xxl flex-grow-1 container-p-y"
          style={{ height: "43px" }}
        >
          <h4 className="card-header">Services</h4>
          <div className="d-flex justify-content-end">
            <input
              type="text"
              placeholder="Search"
              className="form-control me-3 mb-4"
              style={{
                maxWidth: "300px",
                outline: "none",
                boxShadow: "none",
                border: "1px solid #ddd",
              }}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyPress={handleKeyPress}
            />
            <button
              className="btn btn-primary me-2 mb-4"
              onClick={handleSearch}
            >
              Search
            </button>
            <button
              className="btn btn-primary me-5 mb-4"
              onClick={() => navigate("/addService")}
            >
              Add Service
            </button>
          </div>
          <div className="card-datatable table-responsive">
            <table className="invoice-list-table table border-top dataTable no-footer dtr-column mb-5">
              <thead>
                <tr>
                  <th>Sno</th>
                  <th>Name</th>
                  <th>Short Code</th>
                  <th>Status</th>
                  <th>Created At</th>
                  <th>Updated At</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {services?.map((service, index) => {
                  const serialNumber =
                    (currentPage - 1) * (pagination?.per_page || 10) +
                    (index + 1);
                  return (
                    <tr key={service.id}>
                      <td>{serialNumber}</td>
                      <td>{service?.name}</td>
                      <td>{service?.short_code || "-"}</td>
                      <td>
                        <span className={`badge ${getStatusBadge(service?.status)}`}>
                          {getStatusText(service?.status)}
                        </span>
                      </td>
                      <td>
                        {service?.created_at
                          ? moment(service.created_at).format("DD-MM-YYYY")
                          : "-"}
                      </td>
                      <td>
                        {service?.updated_at
                          ? moment(service.updated_at).format("DD-MM-YYYY")
                          : "-"}
                      </td>
                      <td>
                        <FontAwesomeIcon
                          title="Edit"
                          icon={faPencil}
                          className="me-3"
                          style={{ cursor: "pointer" }}
                          onClick={() =>
                            navigate(`/editService/${service.id}`)
                          }
                        />
                        <FontAwesomeIcon
                          icon={faTrash}
                          style={{ cursor: "pointer", color: "#dc3545" }}
                          onClick={() => openDeleteModal(service.id)}
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {services.length === 0 && (
              <div className="text-center py-4">
                <p className="text-muted">No services found</p>
              </div>
            )}
            {pagination?.total > 0 && (
              <Pagination
                totalPages={pagination.total}
                perPage={pagination.per_page}
                handleFunction={(page) => fetchServices(page, search)}
              />
            )}
          </div>
        </div>
      </div>
      {deleteModal && (
        <div className="modal show d-block" tabIndex={-1}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  Are you sure you want to delete this service?
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setDeleteModal(false)}
                ></button>
              </div>
              <div className="modal-footer">
                <button
                  className="btn btn-primary"
                  onClick={() => setDeleteModal(false)}
                >
                  Cancel
                </button>
                <button
                  className="btn btn-danger"
                  onClick={handleDeleteModelConfirmation}
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </Container>
  );
};

